import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  LayoutDashboard, 
  Users, 
  ClipboardList, 
  Truck, 
  FileBarChart,
  LogOut,
  Menu,
  UserCog,
  Wallet,
  Package,
  Settings,
  Globe,
  Camera,
  Factory,
  FileText,
  Receipt,
  Scale,
  Calculator,
  UserCheck,
  HandCoins,
  ChevronUp,
  ChevronDown,
  MoreHorizontal,
  Shield,
  ArrowRightFromLine,
  AlertTriangle,
  MessageCircle,
  Bell,
  Clock,
  X,
  Link,
  Megaphone
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import axios from 'axios';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

const Layout = () => {
  const { user, logout, isAdmin, hasPermission, isImpersonating, impersonatedBakeryName, stopImpersonation, API_URL, getAuthHeader } = useAuth();
  const { t, language, setLanguage, languageNames, languageFlags, availableLanguages } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navContainerRef = useRef(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState(new Set());

  // Fetch notifications and announcements
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) return;
      try {
        const [notifRes, countRes] = await Promise.all([
          axios.get(`${API_URL}/api/notifications`, { headers: getAuthHeader() }),
          axios.get(`${API_URL}/api/notifications/unread-count`, { headers: getAuthHeader() })
        ]);
        setNotifications(notifRes.data || []);
        setUnreadCount(countRes.data?.count || 0);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };
    
    const fetchAnnouncements = async () => {
      if (!user || user.is_super_admin) return;
      try {
        const res = await axios.get(`${API_URL}/api/announcements`, { headers: getAuthHeader() });
        const unreadAnnouncements = (res.data || []).filter(a => !a.is_read && a.is_active);
        setAnnouncements(unreadAnnouncements);
      } catch (error) {
        console.error('Error fetching announcements:', error);
      }
    };
    
    fetchNotifications();
    fetchAnnouncements();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications();
      fetchAnnouncements();
    }, 30000);
    return () => clearInterval(interval);
  }, [user, API_URL]);

  const dismissAnnouncement = async (announcementId) => {
    try {
      await axios.post(`${API_URL}/api/announcements/${announcementId}/read`, {}, { headers: getAuthHeader() });
      setDismissedAnnouncements(prev => new Set([...prev, announcementId]));
      setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
    } catch (error) {
      console.error('Error dismissing announcement:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${notificationId}/read`, {}, { headers: getAuthHeader() });
      setNotifications(prev => prev.map(n => n.id === notificationId ? {...n, is_read: true} : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`, {}, { headers: getAuthHeader() });
      setNotifications(prev => prev.map(n => ({...n, is_read: true})));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Handle return from impersonation
  const handleStopImpersonation = async () => {
    const success = await stopImpersonation();
    if (success) {
      // stopImpersonation will redirect to /settings for super admin
      // This navigate is for regular admin impersonation
      navigate('/super-admin');
    }
  };

  // Check scroll position for arrow visibility
  const checkScrollPosition = () => {
    const container = navContainerRef.current;
    if (container) {
      setCanScrollUp(container.scrollTop > 10);
      setCanScrollDown(container.scrollTop < container.scrollHeight - container.clientHeight - 10);
    }
  };

  useEffect(() => {
    const container = navContainerRef.current;
    if (container) {
      checkScrollPosition();
      container.addEventListener('scroll', checkScrollPosition);
      window.addEventListener('resize', checkScrollPosition);
      return () => {
        container.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
      };
    }
  }, []);

  const scrollNav = (direction) => {
    const container = navContainerRef.current;
    if (container) {
      const scrollAmount = 200;
      container.scrollBy({
        top: direction === 'up' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Expanded sections state
  const [expandedSections, setExpandedSections] = useState(['main']);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // Check if language is RTL
  const isRTL = ['ar', 'ku'].includes(language);

  // Define grouped nav items
  const navSections = [
    {
      id: 'main',
      label: t('main') || 'الرئيسية',
      icon: LayoutDashboard,
      items: [
        { path: '/dashboard', label: t('dashboard'), icon: LayoutDashboard, permission: null },
        { path: '/my-shift', label: t('myShift') || 'دوامي اليوم', icon: Clock, staffOnly: true },
      ]
    },
    {
      id: 'customers',
      label: t('customers') || 'الزبائن',
      icon: Users,
      items: [
        { path: '/customers', label: t('customers'), icon: Users, permission: 'view_customers' },
      ]
    },
    {
      id: 'orders',
      label: t('orders') || 'الطلبات',
      icon: ClipboardList,
      items: [
        { path: '/orders', label: t('orders'), icon: ClipboardList, permission: 'view_orders' },
        { path: '/products', label: t('products'), icon: Package, adminOnly: true },
      ]
    },
    {
      id: 'delivery',
      label: t('delivery') || 'التوصيل',
      icon: Truck,
      items: [
        { path: '/delivery', label: t('deliverySheet') || 'ورقة التوصيل', icon: Truck, permission: 'view_delivery' },
        { path: '/drivers', label: t('drivers'), icon: UserCog, adminOnly: true },
        { path: '/delivery-proofs', label: t('deliveryProofs'), icon: Camera, permission: 'view_delivery' },
      ]
    },
    {
      id: 'production',
      label: t('production') || 'الإنتاج',
      icon: Factory,
      items: [
        { path: '/production', label: t('production'), icon: Factory, permission: 'view_production' },
        { path: '/production-report', label: t('productionReport'), icon: FileBarChart, permission: 'view_production_report' },
      ]
    },
    {
      id: 'finance',
      label: t('finance') || 'المالية',
      icon: Scale,
      items: [
        { path: '/financials', label: t('financials') || 'المستحقات والمصروفات', icon: Scale, permission: 'view_financial_dues' },
        { path: '/profit-report', label: t('profitReport'), icon: Calculator, permission: 'view_profit_report' },
        { path: '/withdrawals', label: t('withdrawals'), icon: HandCoins, adminOnly: true },
        { path: '/employees', label: t('employees'), icon: UserCheck, adminOnly: true },
      ]
    },
    {
      id: 'invoices',
      label: t('invoices') || 'الفواتير',
      icon: Receipt,
      items: [
        { path: '/invoices', label: t('invoices'), icon: FileText, permission: 'view_invoices' },
        { path: '/german-invoices', label: t('germanInvoices'), icon: Receipt, permission: 'view_invoices' },
      ]
    },
    {
      id: 'reports',
      label: t('reports') || 'التقارير',
      icon: FileBarChart,
      items: [
        { path: '/reports', label: t('reports'), icon: FileBarChart, permission: null },
        { path: '/attendance-report', label: t('attendanceReport') || 'تقرير الدوام', icon: Clock, adminOnly: true },
        { path: '/audit-logs', label: t('auditLogs') || 'سجل التدقيق', icon: FileText, adminOnly: true },
      ]
    },
    {
      id: 'communication',
      label: t('communication') || 'التواصل',
      icon: MessageCircle,
      items: [
        { path: '/whatsapp-messages', label: t('whatsappMessages') || 'رسائل الواتساب', icon: MessageCircle, adminOnly: true },
        { path: '/whatsapp-connect', label: 'ربط WhatsApp', icon: Link, adminOnly: true },
      ]
    },
    {
      id: 'settings',
      label: t('settings') || 'الإعدادات',
      icon: Settings,
      items: [
        { path: '/staff-users', label: t('staffUsers'), icon: Shield, adminOnly: true },
        { path: '/announcements', label: 'الإعلانات', icon: Megaphone, superAdminOnly: true },
        { path: '/settings', label: t('settings'), icon: Settings, adminOnly: true },
      ]
    },
  ];

  // Check if user is staff (not admin)
  const isStaff = user?.role === 'staff';

  // Check if staff has ONLY attendance permission (shift-only employee)
  const isShiftOnlyEmployee = isStaff && user?.permissions?.view_attendance && 
    !user?.permissions?.view_production && 
    !user?.permissions?.view_customers && 
    !user?.permissions?.view_orders && 
    !user?.permissions?.view_delivery && 
    !user?.permissions?.view_expenses && 
    !user?.permissions?.view_invoices &&
    !user?.permissions?.view_profit_report;

  // Filter sections and items based on permissions
  const filteredSections = navSections.map(section => ({
    ...section,
    items: section.items.filter(item => {
      // If shift-only employee, only show My Shift page
      if (isShiftOnlyEmployee) {
        return item.path === '/my-shift';
      }
      // Super admin only items
      if (item.superAdminOnly && !user?.is_super_admin) return false;
      // Staff-only items (like My Shift) - only show to staff, not admin
      if (item.staffOnly) return isStaff;
      // Admin sees everything except staff-only
      if (isAdmin) return !item.staffOnly;
      // Admin-only items hidden for staff
      if (item.adminOnly) return false;
      // No permission required = visible
      if (!item.permission) return true;
      // Check permission
      return hasPermission(item.permission);
    })
  })).filter(section => section.items.length > 0);

  // Check if any item in section is active
  const isSectionActive = (section) => {
    return section.items.some(item => 
      location.pathname === item.path || location.pathname.startsWith(item.path + '/')
    );
  };

  const NavItem = ({ item, mobile = false }) => {
    const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    const Icon = item.icon;
    
    return (
      <NavLink
        to={item.path}
        data-testid={`nav-${item.path.replace('/', '')}`}
        onClick={() => mobile && setMobileMenuOpen(false)}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
          isActive
            ? 'bg-amber-100 text-amber-700 font-bold'
            : 'text-stone-600 hover:bg-stone-100'
        }`}
      >
        <Icon className="w-5 h-5" />
        <span>{item.label}</span>
      </NavLink>
    );
  };

  const LanguageSelector = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2" data-testid="language-selector">
          <Globe className="w-4 h-4" />
          <span>{languageFlags[language]}</span>
          <span className="hidden sm:inline">{languageNames[language]}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {availableLanguages.map((lang) => (
          <DropdownMenuItem 
            key={lang} 
            onClick={() => setLanguage(lang)}
            className={`gap-2 ${language === lang ? 'bg-amber-50 text-amber-700' : ''}`}
            data-testid={`lang-${lang}`}
          >
            <span>{languageFlags[lang]}</span>
            <span>{languageNames[lang]}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="no-print fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-2 px-4 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 animate-pulse" />
            <span className="font-medium">
              أنت الآن في حساب: <strong>{impersonatedBakeryName}</strong>
            </span>
            <span className="text-amber-100 text-sm">(وضع المعاينة - لا يتم تسجيل العمليات)</span>
          </div>
          <Button 
            onClick={handleStopImpersonation}
            variant="secondary"
            size="sm"
            className="gap-2 bg-white text-amber-600 hover:bg-amber-50"
          >
            <ArrowRightFromLine className="w-4 h-4" />
            العودة للوحة الرئيسية
          </Button>
        </div>
      )}

      {/* Announcements Banner */}
      {announcements.length > 0 && !isImpersonating && (
        <div className={`no-print ${isImpersonating ? 'mt-12' : ''}`}>
          {announcements.slice(0, 2).map((announcement) => (
            <div 
              key={announcement.id}
              className={`border-b px-4 py-3 flex items-start justify-between gap-4 ${
                announcement.priority === 'urgent' ? 'bg-red-50 border-red-200 text-red-800' :
                announcement.priority === 'important' ? 'bg-orange-50 border-orange-200 text-orange-800' :
                'bg-blue-50 border-blue-200 text-blue-800'
              }`}
            >
              <div className="flex items-start gap-3 flex-1">
                <Megaphone className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                  announcement.priority === 'urgent' ? 'text-red-500' :
                  announcement.priority === 'important' ? 'text-orange-500' :
                  'text-blue-500'
                }`} />
                <div>
                  <h4 className="font-bold text-sm">{announcement.title}</h4>
                  <p className="text-sm opacity-90 whitespace-pre-wrap">{announcement.message}</p>
                </div>
              </div>
              <button 
                onClick={() => dismissAnnouncement(announcement.id)}
                className="text-stone-400 hover:text-stone-600 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Desktop Sidebar - Position based on language direction */}
      <aside className={`no-print hidden md:flex md:flex-col md:w-64 md:bg-white md:border-stone-200 md:h-screen md:fixed md:top-0 z-40 ${
        isRTL ? 'md:right-0 md:border-l' : 'md:left-0 md:border-r'
      } ${isImpersonating ? 'md:top-12' : ''}`}
        style={isImpersonating ? { height: 'calc(100vh - 48px)' } : {}}
      >
        {/* Logo */}
        <div className="p-6 border-b border-stone-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h1 className="font-bold text-stone-900">{t('appName')}</h1>
              <p className="text-xs text-stone-500">{user?.name}</p>
            </div>
          </div>
        </div>

        {/* Language Selector */}
        <div className="p-3 border-b border-stone-200 flex items-center justify-between">
          <LanguageSelector />
          
          {/* Desktop Notifications Bell */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
              <div className="p-3 border-b flex items-center justify-between">
                <h3 className="font-bold">الإشعارات</h3>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs">
                    تحديد الكل كمقروء
                  </Button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-6 text-center text-stone-500">
                    <Bell className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>لا توجد إشعارات</p>
                  </div>
                ) : (
                  notifications.slice(0, 10).map((notif) => (
                    <div 
                      key={notif.id}
                      className={`p-3 border-b hover:bg-stone-50 cursor-pointer ${!notif.is_read ? 'bg-amber-50' : ''}`}
                      onClick={() => markAsRead(notif.id)}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`w-2 h-2 rounded-full mt-2 ${!notif.is_read ? 'bg-amber-500' : 'bg-stone-300'}`} />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{notif.title}</p>
                          <p className="text-xs text-stone-500">{notif.message}</p>
                          <p className="text-xs text-stone-400 mt-1">
                            {new Date(notif.created_at).toLocaleString('ar')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Scroll Up Arrow */}
        {canScrollUp && (
          <button
            onClick={() => scrollNav('up')}
            className="w-full py-2 bg-gradient-to-b from-amber-50 to-transparent flex justify-center items-center text-amber-600 hover:text-amber-700 transition-colors sticky top-0 z-10"
            data-testid="nav-scroll-up"
          >
            <ChevronUp className="w-6 h-6 animate-bounce" />
          </button>
        )}

        {/* Navigation - Grouped Sections */}
        <nav 
          ref={navContainerRef}
          className="flex-1 p-3 space-y-1 overflow-y-auto scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {filteredSections.map((section) => {
            const SectionIcon = section.icon;
            const isExpanded = expandedSections.includes(section.id);
            const isActive = isSectionActive(section);
            
            // If section has only one item, show it directly
            if (section.items.length === 1) {
              return <NavItem key={section.id} item={section.items[0]} />;
            }
            
            return (
              <div key={section.id} className="mb-1">
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all ${
                    isActive
                      ? 'bg-amber-100 text-amber-700'
                      : 'text-stone-600 hover:bg-stone-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <SectionIcon className="w-5 h-5" />
                    <span className="font-medium">{section.label}</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Section Items */}
                {isExpanded && (
                  <div className={`mt-1 space-y-0.5 ${isRTL ? 'mr-4 pr-3 border-r-2' : 'ml-4 pl-3 border-l-2'} border-stone-200`}>
                    {section.items.map((item) => {
                      const ItemIcon = item.icon;
                      const itemActive = location.pathname === item.path;
                      return (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-all ${
                            itemActive
                              ? 'bg-amber-50 text-amber-700 font-medium'
                              : 'text-stone-500 hover:bg-stone-50 hover:text-stone-700'
                          }`}
                        >
                          <ItemIcon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Scroll Down Arrow */}
        {canScrollDown && (
          <button
            onClick={() => scrollNav('down')}
            className="w-full py-2 bg-gradient-to-t from-amber-50 to-transparent flex justify-center items-center text-amber-600 hover:text-amber-700 transition-colors sticky bottom-0 z-10"
            data-testid="nav-scroll-down"
          >
            <ChevronDown className="w-6 h-6 animate-bounce" />
          </button>
        )}

        {/* Logout Button */}
        <div className="p-4 border-t border-stone-200">
          <Button
            variant="ghost"
            data-testid="logout-btn"
            onClick={logout}
            className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <LogOut className="w-5 h-5 me-3" />
            {t('logout')}
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="no-print md:hidden fixed top-0 left-0 right-0 bg-white border-b border-stone-200 z-40 px-4 py-3">
        <div className="flex items-center justify-between">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 flex flex-col p-0">
              <SheetHeader className="p-4 border-b flex-shrink-0">
                <SheetTitle className="text-right">{t('appName')}</SheetTitle>
              </SheetHeader>
              
              {/* Mobile Language Selector */}
              <div className="px-4 py-2 flex-shrink-0">
                <LanguageSelector />
              </div>
              
              <nav className="flex-1 overflow-y-auto px-4 py-2 space-y-1">
                {filteredSections.map((section) => {
                  const SectionIcon = section.icon;
                  const isExpanded = expandedSections.includes(section.id);
                  
                  if (section.items.length === 1) {
                    return <NavItem key={section.id} item={section.items[0]} mobile />;
                  }
                  
                  return (
                    <div key={section.id}>
                      <button
                        onClick={() => toggleSection(section.id)}
                        className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-stone-600 hover:bg-stone-100"
                      >
                        <div className="flex items-center gap-3">
                          <SectionIcon className="w-5 h-5" />
                          <span className="font-medium">{section.label}</span>
                        </div>
                        <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                      {isExpanded && (
                        <div className="mt-1 mr-4 pr-3 border-r-2 border-stone-200 space-y-0.5">
                          {section.items.map((item) => (
                            <NavItem key={item.path} item={item} mobile />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
              
              {/* Mobile Logout - Fixed at bottom */}
              <div className="p-4 border-t border-stone-200 flex-shrink-0 bg-white">
                <Button
                  variant="ghost"
                  data-testid="mobile-logout-btn"
                  onClick={() => {
                    logout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <LogOut className="w-5 h-5 me-3" />
                  {t('logout')}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          
          <h1 className="font-bold text-stone-900">{t('appName')}</h1>
          
          <div className="flex items-center gap-2">
            {/* Notifications Bell */}
            <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative">
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="p-3 border-b flex items-center justify-between">
                  <h3 className="font-bold">الإشعارات</h3>
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs">
                      تحديد الكل كمقروء
                    </Button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center text-stone-500">
                      <Bell className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p>لا توجد إشعارات</p>
                    </div>
                  ) : (
                    notifications.slice(0, 10).map((notif) => (
                      <div 
                        key={notif.id}
                        className={`p-3 border-b hover:bg-stone-50 cursor-pointer ${!notif.is_read ? 'bg-amber-50' : ''}`}
                        onClick={() => markAsRead(notif.id)}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`w-2 h-2 rounded-full mt-2 ${!notif.is_read ? 'bg-amber-500' : 'bg-stone-300'}`} />
                          <div className="flex-1">
                            <p className="font-medium text-sm">{notif.title}</p>
                            <p className="text-xs text-stone-500">{notif.message}</p>
                            <p className="text-xs text-stone-400 mt-1">
                              {new Date(notif.created_at).toLocaleString('ar')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
            
            <LanguageSelector />
          </div>
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      <nav className="no-print md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 z-40 px-1 pb-safe shadow-lg">
        <div className="flex justify-around items-center">
          {/* Shift-only employee sees only My Shift */}
          {isShiftOnlyEmployee ? (
            <NavLink
              to="/my-shift"
              data-testid="mobile-nav-my-shift"
              className={`flex flex-col items-center py-3 px-4 rounded-lg transition-all ${
                location.pathname === '/my-shift' ? 'text-amber-600 bg-amber-50' : 'text-stone-500 hover:bg-stone-50'
              }`}
            >
              <Clock className="w-6 h-6" />
              <span className="text-xs mt-1">دوامي اليوم</span>
            </NavLink>
          ) : (
            <>
              {/* Show main nav items for mobile */}
              {[
                { path: '/dashboard', label: t('dashboard'), icon: LayoutDashboard },
                { path: '/orders', label: t('orders'), icon: ClipboardList },
                { path: '/customers', label: t('customers'), icon: Users },
                { path: '/delivery', label: t('delivery'), icon: Truck },
              ].map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    data-testid={`mobile-nav-${item.path.replace('/', '')}`}
                    className={`flex flex-col items-center py-2 px-2 min-w-[56px] rounded-lg transition-all ${
                      isActive ? 'text-amber-600 bg-amber-50' : 'text-stone-500 hover:bg-stone-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-[9px] mt-1 truncate max-w-[50px]">{item.label}</span>
                  </NavLink>
                );
              })}
              
              {/* More Menu Button */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="mobile-more-menu"
                    className="flex flex-col items-center py-2 px-2 min-w-[56px] rounded-lg transition-all text-stone-500 hover:bg-stone-50"
                  >
                    <MoreHorizontal className="w-5 h-5" />
                    <span className="text-[9px] mt-1">{t('more') || 'المزيد'}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  side="top" 
                  className="w-56 max-h-[60vh] overflow-y-auto mb-2"
                >
                  {filteredSections.slice(3).map((section) => {
                    return section.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <DropdownMenuItem key={item.path} asChild>
                          <NavLink
                            to={item.path}
                            data-testid={`mobile-more-${item.path.replace('/', '')}`}
                            className={`flex items-center gap-3 w-full ${
                              isActive ? 'bg-amber-50 text-amber-700' : ''
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            <span>{item.label}</span>
                          </NavLink>
                        </DropdownMenuItem>
                      );
                    });
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </nav>

      {/* Main Content - Position based on language direction */}
      <main className={`pt-16 md:pt-0 pb-20 md:pb-0 min-h-screen ${
        isRTL ? 'md:mr-64' : 'md:ml-64'
      } ${isImpersonating ? 'mt-12' : ''}`}>
        <div className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
